import { queryOptions } from "@tanstack/react-query";
import {
  getAdminActivitiesOverview,
  getSchoolActivitiesOverview,
} from "#/modules/beweegactiviteitenreeks/activities.functions";
import type { TenantQueryKey } from "#/modules/util.fetch-client/tenant-query-key";
import type { ActivitiesSearch, AdminActivitiesSearch } from "./schema.search";

/**
 * Typed filter + pagination values for the school activities overview. Built from the page's typed
 * `Route.useSearch()` (component) or from the validated `location.search` (route loader) — both feed
 * the SAME query-options factory so the query key is identical for a given URL.
 */
export type ActivitiesOverviewFilters = ActivitiesSearch;

/** Follows the tenant segment — invalidate `[tenant, ACTIVITIES_OVERVIEW_QUERY_KEY]` to refresh every filter variant. */
export const ACTIVITIES_OVERVIEW_QUERY_KEY = "activities-overview";

// The route loader primes THIS query via `queryClient.ensureQueryData` and the ssr-query
// integration dehydrates it into the SSR payload, so the client hydrates the entry with the real
// server fetch time. This staleTime is therefore honest: no refetch right after hydration, a
// background refetch on remount once the data is actually older than 30s. Mutations invalidate
// explicitly, and every distinct filter/sort/page produces a new key that fetches on its own.
const OVERVIEW_STALE_TIME = 30_000;

export function activitiesOverviewQueryOptions(
  tenant: TenantQueryKey,
  filters: ActivitiesOverviewFilters,
) {
  return queryOptions({
    // The server fn resolves the school from the page URL, not from `filters` — the tenant
    // segment is what keeps two schools' lists from colliding under one cache key.
    queryKey: [tenant, ACTIVITIES_OVERVIEW_QUERY_KEY, filters] as const,
    queryFn: () => getSchoolActivitiesOverview({ data: filters }),
    staleTime: OVERVIEW_STALE_TIME,
  });
}

// Sentinel tenant segment for the admin list — keeps the `[tenant, root, filters]` key shape, so
// the tenant-less `tenantQueryFilter` predicate (root key at index 1) invalidates this entry too.
const ADMIN_TENANT_KEY = "admin";

export function adminActivitiesOverviewQueryOptions(filters: AdminActivitiesSearch) {
  return queryOptions({
    queryKey: [ADMIN_TENANT_KEY, ACTIVITIES_OVERVIEW_QUERY_KEY, filters] as const,
    queryFn: () => getAdminActivitiesOverview({ data: filters }),
    staleTime: OVERVIEW_STALE_TIME,
  });
}
